import { makeFormData } from "utils/makeFormData";
import { emptyApi } from "./emptyApi";
import { File } from "buffer";

export type RegistrationStatus =
  | "PENDING_DETAILS"
  | "IN_REVIEW"
  | "PROCESSING"
  | "REGISTERED";

export type Registration = {
  state_registration_id: string;
  state: string;
  tax_detail_id?: string;
  request_date: string;
  status: RegistrationStatus;
  state_id: string;
  application?:
    | string
    | {
        file_name?: string;
        file_id?: string;
      };
  registration_license:
    | string
    | {
        file_name?: string;
        file_id?: string;
      };
};

type payment_schedule = "Monthly" | "Quarterly" | "Semi-annually" | "Annually";

export type AddRegistrationDetailsPayload = {
  sales_tax_id: string;
  payment_schedule: payment_schedule;
  filing_start_date: string;
  registration_license:
    | string
    | {
        file_name?: string;
        file_id?: string;
      };
  application?: string;
};

export type AddExistingRegistrationPayload = {
  state_id: string;
  tax_detail_id: string;
  sales_tax_id: string;
  payment_schedule: payment_schedule;
  filing_start_date: string;
  registration_license:
    | File
    | {
        file_name?: string;
        file_id?: string;
      };
  application: {
    file_name?: string;
    file_id?: string;
  };
};

export type SalesTaxRegistrationUpdatePayload = {
  filing_start_date: string;
  tax_detail_id?: string;
};

export type PreSalesTaxRegistrationPayload =
  SalesTaxRegistrationUpdatePayload & {
    state_id: string;
  };

export type PostSalesRegistrationPayload = {
  sales_tax_id?: string;
  payment_schedule: payment_schedule;
  filing_start_date: string;
  registration_license_id: string;
  tax_detail_id?: string;
  application?:
    | File
    | {
        file_name?: string;
        file_id?: string;
      };
};
export type RegistrationDetailsResponse = {
  state_registration_id: string;
  request_date: string;
  status: RegistrationStatus;
  state: string;
  state_id: string;
  sales_tax_id: string;
  payment_schedule:
    | "ANNUALLY"
    | "MONTHLY"
    | "QUARTERLY"
    | "SEMI_ANNUALLY"
    | "NONE";
  filing_start_date: string;
  registration_license?: {
    file_name: string;
    file_id: string;
  };
  application?: {
    file_name: string;
    file_id: string;
  };
  tax_detail_id?: string;
};

export const salesTaxRegistration = emptyApi.injectEndpoints({
  endpoints: (build) => ({
    getSalesTaxRegistrations: build.query<Registration[], { entityId: string }>(
      {
        query: ({ entityId }) => ({
          url: `/api/inkle/salestax/entity/${entityId}/registrations/`,
        }),
        providesTags: ["SALES_TAX_REGISTRATIONS"],
      }
    ),
    updatePreSalesTaxRegistrations: build.mutation<
      Registration[],
      {
        entityId: string;
        state_registration_id: string;
        payload: SalesTaxRegistrationUpdatePayload;
      }
    >({
      query: ({ entityId, payload, state_registration_id }) => ({
        url: `/api/inkle/salestax/entity/${entityId}/registrations/pre/${state_registration_id}`,
        method: "PUT",
        body: payload,
      }),
      invalidatesTags: ["SALES_TAX_REGISTRATIONS"],
    }),

    createPreSalesRegistraions: build.mutation<
      Registration,
      {
        entityId: string;
        payload: PreSalesTaxRegistrationPayload;
      }
    >({
      query: ({ entityId, payload }) => {
        return {
          url: `/api/inkle/salestax/entity/${entityId}/registrations/pre/`,
          method: "POST",
          body: payload,
        };
      },
      invalidatesTags: ["SALES_TAX_REGISTRATIONS"],
    }),

    updatePostSalesTaxRegistrations: build.mutation<
      Registration[],
      {
        entityId: string;
        state_registration_id: string;
        payload: SalesTaxRegistrationUpdatePayload;
      }
    >({
      query: ({ entityId, payload, state_registration_id }) => {
        const formData = makeFormData(payload);
        return {
          url: `/api/inkle/salestax/entity/${entityId}/registrations/post/${state_registration_id}`,
          method: "PUT",
          body: formData,
        };
      },
      invalidatesTags: ["SALES_TAX_REGISTRATIONS"],
    }),

    createPostSalesRegistrations: build.mutation<
      Registration,
      {
        entityId: string;
        payload: PostSalesRegistrationPayload;
      }
    >({
      query: ({ entityId, payload }) => {
        const formData = makeFormData(payload);
        return {
          url: `/api/inkle/salestax/entity/${entityId}/registrations/post/`,
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: (result) => (result ? ["SALES_TAX_REGISTRATIONS"] : []),
    }),
    getSalesTaxRegistrationDetails: build.query<
      RegistrationDetailsResponse,
      { entityId: string; state_registration_id: string }
    >({
      query: ({ entityId, state_registration_id }) => ({
        url: `/api/inkle/salestax/entity/${entityId}/registrations/${state_registration_id}`,
      }),
    }),
  }),
});

export const {
  useGetSalesTaxRegistrationsQuery,
  useUpdatePreSalesTaxRegistrationsMutation,
  useCreatePreSalesRegistraionsMutation,
  useCreatePostSalesRegistrationsMutation,
  useGetSalesTaxRegistrationDetailsQuery,
  useUpdatePostSalesTaxRegistrationsMutation,
} = salesTaxRegistration;
